/**
 * Application-wide constants
 * Migrated from rate-loader-abyss-migration (SOURCE)
 * @module constants
 */

// ====================
// User Session Constants
// ====================

export const TEST_USER_ID = 'test-user-001';
export const TEST_SESSION_PREFIX = 'test-session';

/** Prefix for internally-generated session IDs (New Chat, Back from History, etc.).
 *  Parent-provided session IDs (from widget-ui) do NOT use this prefix. */
export const INTERNAL_SESSION_PREFIX = 'session';

export const generateTestSessionId = (): string => {
  return `${TEST_SESSION_PREFIX}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/** Generate a unique internal session ID with timestamp + random suffix.
 *  Mirrors the rate-loader pattern to avoid collisions on rapid New Chats. */
export const generateSessionId = (): string => {
  return `${INTERNAL_SESSION_PREFIX}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/** Returns true if the sessionId was generated internally (not provided by parent). */
export const isInternalSession = (sid: string | null): boolean => {
  if (!sid) return true;
  return sid.startsWith(`${INTERNAL_SESSION_PREFIX}_`) || sid.startsWith(`${TEST_SESSION_PREFIX}-`);
};

// ====================
// Workflow Constants
// ====================

export const WORKFLOW_TYPES = {
  NEW_FS: 'newFs',
  UPDATE_FS: 'updateFs',
  AUDIT: 'audit',
} as const;

export const WORKFLOW_STEPS = {
  COLUMN_MAPPING: 'columnMapping',
  VALIDATION: 'validation',
  LOAD: 'load',
} as const;

export const SCREEN_TYPES = {
  HERO: 'hero',
  DASHBOARD: 'dashboard',
} as const;

// ====================
// File Upload Constants
// ====================

export const FILE_PURPOSES = {
  ROSTER: 'Roster',
  MISC: 'Miscellaneous',
} as const;

export const FILE_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  ERROR: 'error',
} as const;

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const ACCEPTED_FILE_TYPES = {
  PDF: 'application/pdf',
  EXCEL: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  CSV: 'text/csv',
} as const;

// ====================
// API Constants
// ====================

export const API_TIMEOUT = 900000; // 15 minutes for long-running ops
export const API_RETRY_ATTEMPTS = 3;
export const API_RETRY_DELAY = 1000; // 1 second

// ====================
// Storage Keys
// ====================

export const STORAGE_KEYS = {
  SESSION_ID: 'rateloader_session_id',
  USER_ID: 'rateloader_user_id',
  WORKFLOW_STATE: 'rateloader_workflow_state',
  FILE_METADATA: 'rateloader_file_metadata',
} as const;

export const INDEXEDDB = {
  DATABASE_NAME: 'RateLoaderDB',
  VERSION: 1,
  STORES: {
    FILES: 'uploadedFiles',
    METADATA: 'metadata',
  },
} as const;

// ====================
// Validation Constants
// ====================

export const VALIDATION = {
  JOB_NUMBER: {
    MIN_LENGTH: 4,
    MAX_LENGTH: 20,
    PATTERN: /^[A-Za-z0-9-]+$/,
  },
} as const;

export const FILE_SIZE_LIMITS = {
  EXCEL: 50 * 1024 * 1024,
  PDF: 100 * 1024 * 1024,
  CSV: 25 * 1024 * 1024,
} as const;

export const FILE_TYPES = {
  EXCEL: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ],
  PDF: ['application/pdf'],
} as const;


// ====================
// Feature Flags
// ====================

export const FEATURE_FLAGS = {
  ENABLE_ANALYTICS: true,
  ENABLE_ERROR_REPORTING: true,
  ENABLE_DEBUG_MODE: import.meta.env.DEV,
  ENABLE_MOCK_API: import.meta.env.VITE_USE_MOCK_API === 'true',
  // Migration feature flags
  ENABLE_CORE_FLOW: import.meta.env.VITE_FEATURE_CORE_FLOW !== 'false',
  ENABLE_PROVIDER_CONSOLE_FLOW: import.meta.env.VITE_FEATURE_PROVIDER_CONSOLE_FLOW !== 'false',
  ENABLE_RESUME: import.meta.env.VITE_FEATURE_RESUME !== 'false',
  ENABLE_FEEDBACK: import.meta.env.VITE_FEATURE_FEEDBACK !== 'false',
} as const;

/**
 * Check if a feature is enabled via env var
 */
export function isFeatureEnabled(feature: string): boolean {
  const envKey = `VITE_FEATURE_${feature.toUpperCase()}`;
  return (import.meta.env as any)[envKey] !== 'false';
}

// ====================
// Error Messages
// ====================

export const ERROR_MESSAGES = {
  FILE_UPLOAD: {
    TOO_LARGE: `File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit`,
    INVALID_TYPE: 'Invalid file type. Please upload PDF, Excel, or CSV files.',
    UPLOAD_FAILED: 'File upload failed. Please try again.',
    NO_FILES_SELECTED: 'Please select at least one file to upload.',
  },
  API: {
    NETWORK_ERROR: 'Network error. Please check your connection.',
    TIMEOUT: 'Request timed out. Please try again.',
    SERVER_ERROR: 'Server error. Please contact support.',
  },
  VALIDATION: {
    INVALID_JOB_NUMBER: 'Invalid job number format.',
  },
  GENERAL: {
    UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
  },
} as const;

export const SUCCESS_MESSAGES = {
  FILE_UPLOAD: 'Files uploaded successfully',
  SAVE_COMPLETE: 'Data saved successfully',
  ANALYSIS_COMPLETE: 'Analysis completed successfully',
  WORKFLOW_COMPLETE: 'Workflow completed successfully',
} as const;

// ====================
// Type Exports
// ====================

export type WorkflowType = typeof WORKFLOW_TYPES[keyof typeof WORKFLOW_TYPES];
export type WorkflowStep = typeof WORKFLOW_STEPS[keyof typeof WORKFLOW_STEPS];
export type ScreenType = typeof SCREEN_TYPES[keyof typeof SCREEN_TYPES];
export type FilePurposeType = typeof FILE_PURPOSES[keyof typeof FILE_PURPOSES];
export type FileStatusType = typeof FILE_STATUS[keyof typeof FILE_STATUS];
 